import {
  distributionInput,
  distributionTexts,
  stopSchema,
} from "features/CenterContent/shared/operationalInformation/utils";
import { FieldError, FieldErrors } from "react-hook-form";
import { z } from "zod";

export const stationThreatLevelEnum = z.enum(
  ["ALPHA", "BRAVO", "CHARLIE", "DELTA"],
  {
    errorMap: () => ({
      message: "Du må velge trusselnivå for å kunne opprette hendelsen",
    }),
  },
);

export type StationThreatLevelEnum = z.infer<typeof stationThreatLevelEnum>;

const stationThreatLevelSchema = z.object({
  stationInformationType: z.literal("STATION_THREAT_LEVEL"),
  stops: stopSchema.array().min(1),
  threatLevel: stationThreatLevelEnum,
});

export const stationMeansOfPaymentEnum = z.enum(["COINS", "BANKNOTE", "CARD"], {
  errorMap: () => ({
    message: "Du må velge et betalingsvalg for å kunne opprette hendelsen",
  }),
});

export type StationMeansOfPaymentEnum = z.infer<
  typeof stationMeansOfPaymentEnum
>;

const stationSalesChannelReducedSchema = z.object({
  stationInformationType: z.literal("STATION_SALES_CHANNEL_REDUCED"),
  stops: stopSchema.array().min(1),
  meansOfPayment: stationMeansOfPaymentEnum,
});

const stationPersonSchema = z.object({
  stationInformationType: z.enum([
    "STATION_DIFFICULT_CUSTOMER",
    "STATION_WANTED_PERSON",
  ]),
  stops: stopSchema.array().min(1),
  personDescription: distributionTexts,
});

const stationSingleStopSchema = z.object({
  stationInformationType: z.enum([
    "STATION_ELEVATOR_FAULT",
    "STATION_SPEAKER_FAULT",
    "STATION_TRACK_MONITOR_FAULT",
    "STATION_WHEELCHAIR_RAMP_FAULT",
  ]),
  stop: stopSchema,
  affectedTracks: z
    .array(z.number().int().nonnegative(), {
      errorMap: () => ({
        message: "Du må velge minst ett spor for å kunne opprette hendelsen",
      }),
    })
    .min(1),
});

const arrivalAndDepartureMonitorFaultSchema = z.object({
  stationInformationType: z.literal(
    "STATION_ARRIVAL_AND_DEPARTURE_MONITOR_FAULT",
  ),
  stop: stopSchema,
  affectedTracks: z.array(z.number()).optional(),
});

export const stationCommunicationSystemsEnum = z.enum([
  "ARRIVAL_AND_DEPARTURE_MONITORS",
  "SPEAKERS",
  "TRACK_MONITORS",
]);

export type StationCommunicationSystemsEnum = z.infer<
  typeof stationCommunicationSystemsEnum
>;

const stationCommunicationSystemsDownSchema = z.object({
  stationInformationType: z.literal("STATION_COMMUNICATION_SYSTEMS_DOWN"),
  stops: stopSchema.array().min(1),
  affectedSystems: z
    .array(stationCommunicationSystemsEnum, {
      errorMap: () => ({
        message:
          "Du må velge et påvirket system for å kunne opprette hendelsen",
      }),
    })
    .min(1),
});

export const dropsOperationLevel = z.enum(["RED", "YELLOW"], {
  errorMap: () => ({ message: "Du må velge et nivå" }),
});

export type DropsOperationLevel = z.infer<typeof dropsOperationLevel>;

const stationDropsOperationReducedSchema = z.object({
  stationInformationType: z.literal("STATION_DROPS_OPERATION"),
  stops: stopSchema.array().min(1),
  level: dropsOperationLevel,
});

export const stationCustomEventSeverityEnum = z.enum(["INFO", "WARNING"], {
  errorMap: () => ({ message: "Du må velge en alvorlighetsgrad" }),
});

export type StationCustomEventSeverityEnum = z.infer<
  typeof stationCustomEventSeverityEnum
>;

const stationCustomSchema = z.object({
  stationInformationType: z.literal("STATION_GENERAL"),
  stops: stopSchema.array().min(1),
  severity: stationCustomEventSeverityEnum,
  distributions: distributionInput,
});

const stationGeneralSchema = z.object({
  stationInformationType: z.enum([
    "STATION_PLATFORM_FAULT",
    "STATION_ALL_SALES_CHANNELS_DOWN",
    "STATION_APP_SALES_CHANNEL_DOWN",
    "STATION_MOBILE_TERMINALS_SALES_CHANNEL_DOWN",
    "STATION_TICKET_MACHINE_DOWN",
    "STATION_TICKET_VALIDATOR_DOWN",
    "STATION_DEPOSIT_MACHINE_DOWN",
    "STATION_MAIN_MONITOR_FAULT",
    "STATION_PLATFORM_SLIPPERY",
    "STATION_PARKING_ISSUE",
    "STATION_ENTRANCE_CHANGED",
    "STATION_WAITING_ROOM_CLOSED",
    "STATION_CLOSED_FOR_PASSENGERS",
    "STATION_DROPS_EVACUATION",
  ]),
  stops: stopSchema.array().min(1),
});

const stationFormSchema = z.discriminatedUnion(
  "stationInformationType",
  [
    stationThreatLevelSchema,
    stationSalesChannelReducedSchema,
    stationPersonSchema,
    stationSingleStopSchema,
    arrivalAndDepartureMonitorFaultSchema,
    stationCommunicationSystemsDownSchema,
    stationDropsOperationReducedSchema,
    stationCustomSchema,
    stationGeneralSchema,
  ],
  {
    errorMap: () => ({
      message: "Du må velge en hendelsestype for å kunne opprette hendelsen",
    }),
  },
);

type StationFormSchema = z.infer<typeof stationFormSchema>;

export type StationInformationType =
  StationFormSchema["stationInformationType"];

export const formSchema = z.object({
  stationForm: stationFormSchema,
});

export type FormSchema = z.infer<typeof formSchema>;

/* React-hook-form uses an intersection type for interacting with fields, but not with their errors.
   This means that although errors are properly populated in js, the type is wrong and only the
   common fields are possible to get errors for without casting.
   The types below allow us to manually tell TS that an error can exist for a field that doesn't
   exist on every subtype of the schema.
   It's unnecessarily difficult but apparently also something they aren't able to fix on their own:
   https://github.com/react-hook-form/react-hook-form/issues/8815
*/
type FormIntersection = z.infer<typeof stationThreatLevelSchema> &
  z.infer<typeof stationSalesChannelReducedSchema> &
  z.infer<typeof stationPersonSchema> &
  z.infer<typeof stationSingleStopSchema> &
  z.infer<typeof stationCommunicationSystemsDownSchema> &
  z.infer<typeof stationDropsOperationReducedSchema> &
  z.infer<typeof stationCustomSchema> &
  z.infer<typeof stationGeneralSchema>;

// For a field that exists on any subtype of StationFormSchema, add that field as a possible
// key that can have a react-hook-form FieldError.
export type FormErrorWithDiscriminatedField<T extends keyof FormIntersection> =
  | (FieldErrors<FormSchema> & {
      [key in T]?: FieldError;
    })
  | undefined;
